import React from "react";
import { Box, Chip, Typography, styled } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { gaEvent } from "../../analytics";
import {
  CATEGORY_LABELS,
  FILTERS_TITLE,
  FILTER_ALL_LABEL,
  FILTER_FAVORITES_LABEL,
} from "../../pages/shared/constants";

export const FAVORITES_FILTER = "__favorites__";

interface ProductFiltersProps {
  categories: string[];
  selected: string | null;
  onSelect: (category: string | null) => void;
  showFavorites?: boolean;
}

const FiltersContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.common.white,
  marginBottom: theme.spacing(2),
}));

/**
 * One row on mobile that scrolls sideways, wrapping only from `sm` up.
 *
 * The negative margin lets the row bleed to the container edge so the last chip visibly clips —
 * without it the row ends flush with the padding and reads as a complete, non-scrollable set.
 */
const ChipsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  overflowX: "auto",
  // Keeps the row's own min-content from leaking up the flex chain and widening the page. The
  // scroll container has to be allowed to be narrower than the chips it holds — that is the point.
  minWidth: 0,
  marginLeft: theme.spacing(-2),
  marginRight: theme.spacing(-2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  [theme.breakpoints.up("sm")]: {
    flexWrap: "wrap",
    overflowX: "visible",
    margin: 0,
    padding: 0,
  },
}));

/**
 * Selection used to be an inset box-shadow plus a weight bump — no colour change at all, which on
 * a phone reads as nothing. It is now a filled navy pill against an outlined one.
 */
const FilterChip = styled(Chip, { shouldForwardProp: (prop) => prop !== "active" })<{
  active: boolean;
}>(({ theme, active }) => ({
  height: 30,
  borderRadius: 999,
  paddingLeft: theme.spacing(0.25),
  paddingRight: theme.spacing(0.25),
  flexShrink: 0,
  fontSize: theme.typography.pxToRem(13),
  fontWeight: active ? 600 : 400,
  backgroundColor: active ? theme.palette.text.primary : "transparent",
  color: active ? theme.palette.common.white : theme.palette.text.primary,
  border: active ? "none" : `1px solid ${theme.palette.divider}`,
  "&:hover": {
    backgroundColor: active ? theme.palette.text.primary : theme.palette.action.hover,
  },
  "& .MuiChip-icon": {
    color: "inherit",
  },
}));

const ProductFilters: React.FC<ProductFiltersProps> = ({
  categories,
  selected,
  onSelect,
  showFavorites,
}) => {
  const handleSelect = (filter: string | null) => {
    const label =
      filter === null
        ? FILTER_ALL_LABEL
        : filter === FAVORITES_FILTER
          ? FILTER_FAVORITES_LABEL
          : filter;
    gaEvent("filter_select", { filter_name: label });
    onSelect(filter);
  };

  if (categories.length === 0) return null;

  return (
    <FiltersContainer>
      {/* The body font, not the theme's condensed `h6` face: this is a UI label sitting directly
          above the controls it describes, and Antonio reads as a marketing headline there. */}
      <Typography variant="h6" fontWeight={700} fontFamily="Inter, sans-serif">
        {FILTERS_TITLE}
      </Typography>
      <ChipsRow>
        <FilterChip
          label={FILTER_ALL_LABEL}
          active={selected === null}
          aria-pressed={selected === null}
          onClick={() => handleSelect(null)}
        />
        {showFavorites && (
          <FilterChip
            icon={<FavoriteIcon sx={{ fontSize: 16 }} />}
            label={FILTER_FAVORITES_LABEL}
            active={selected === FAVORITES_FILTER}
            aria-pressed={selected === FAVORITES_FILTER}
            onClick={() => handleSelect(selected === FAVORITES_FILTER ? null : FAVORITES_FILTER)}
          />
        )}
        {categories.map((cat) => (
          <FilterChip
            key={cat}
            label={CATEGORY_LABELS[cat] ?? cat}
            active={selected === cat}
            aria-pressed={selected === cat}
            onClick={() => handleSelect(selected === cat ? null : cat)}
          />
        ))}
      </ChipsRow>
    </FiltersContainer>
  );
};

export default ProductFilters;
